import {
  SettingOutlined,
  InfoCircleOutlined,
  UndoOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import { Modal } from '../../../modals/default'
import { ITableColumnsType } from '../types'
import { useTableSettings } from '../hooks/use-table-settings'
import styles from './table-settings.module.scss'
import type { ColumnSettingItem } from '../hooks/use-table-settings'
import { Checkbox } from '../../../checkboxes/default'
import { Switch } from '../../../switches/default'
import { Space } from '../../../spaces/default'
import { Button } from '../../../buttons/default'
import { Tooltip } from '../../../tooltips/default'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import React from 'react'

interface TableSettingsProps {
  buttonText?: string
  buttonIcon?: React.ReactNode
  disabled?: boolean
}

export function TableSettings<T = any>({
  buttonText = 'Columns',
  buttonIcon = <SettingOutlined />,
  disabled = false,
}: TableSettingsProps) {
  const {
    open,
    tempSettings,
    initialColumns,
    allSelected,
    someSelected,
    handleOpen,
    handleCancel,
    handleResetToDefault,
    handleSelectAll,
    handleApply,
    handleVisibilityChange,
    handleFixedChange,
  } = useTableSettings<T>()

  return (
    <>
      <Button icon={buttonIcon} disabled={disabled} onClick={handleOpen}>
        {buttonText}
      </Button>

      <Modal
        title='Columns Settings'
        open={open}
        onCancel={handleCancel}
        width={600}
        footer={<ModalFooter onCancel={handleCancel} onResetToDefault={handleResetToDefault} onApply={handleApply} />}
      >
        <ModalBody
          tempSettings={tempSettings}
          initialColumns={initialColumns}
          allSelected={allSelected}
          someSelected={someSelected}
          onVisibilityChange={handleVisibilityChange}
          onFixedChange={handleFixedChange}
          onSelectAll={handleSelectAll}
        />
      </Modal>
    </>
  )
}

interface ModalBodyProps<T> {
  tempSettings: ColumnSettingItem[]
  initialColumns: ITableColumnsType<T>
  allSelected: boolean
  someSelected: boolean
  onVisibilityChange: (key: React.Key, checked: boolean) => void
  onFixedChange: (key: React.Key, checked: boolean) => void
  onSelectAll: (checked: boolean) => void
}

function ModalBody<T>({
  tempSettings,
  initialColumns,
  allSelected,
  someSelected,
  onVisibilityChange,
  onFixedChange,
  onSelectAll,
}: ModalBodyProps<T>) {
  const getDisabledSwitchTooltip = ({ isVisible, isDefaultFixed }: { isVisible: boolean; isDefaultFixed: boolean }) => {
    if (isDefaultFixed) {
      return 'This column is fixed by default'
    } else if (!isVisible) {
      return 'Make the column visible to fix it'
    }
    return ''
  }

  const getDataIndexFieldTooltip = ({ dataIndex }: { dataIndex: ColumnSettingItem['dataIndex'] }) => {
    return <DataIndexTooltip dataIndex={dataIndex} />
  }

  return (
    <div className={styles.settingsContent}>
      <div className={styles.selectAllContainer}>
        <Checkbox checked={allSelected} indeterminate={someSelected} onChange={e => onSelectAll(e.target.checked)}>
          Select All Columns
        </Checkbox>
      </div>
      <div className={styles.columnList}>
        {tempSettings.map(item => {
          const isVisible = item.visible
          const isDefaultFixed = !!initialColumns.find(col => col.key === item.key)?.fixed
          const isDisabled = !isVisible || isDefaultFixed

          // Check if this is the last visible column
          const visibleCount = tempSettings.filter(setting => setting.visible).length
          const isLastVisible = isVisible && visibleCount === 1

          return (
            <div key={item.key} className={styles.columnItem}>
              <div className={styles.columnMain}>
                <Checkbox
                  checked={item.visible}
                  onChange={e => onVisibilityChange(item.key, e.target.checked)}
                  disabled={isLastVisible}
                  title={isLastVisible ? 'At least one column must remain visible' : undefined}
                >
                  {item.title}
                </Checkbox>
                <Tooltip title={getDataIndexFieldTooltip({ dataIndex: item.dataIndex })}>
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              </div>
              <div className={styles.columnActions}>
                <span className={styles.fixedLabel}>Fixed:</span>
                <Tooltip title={getDisabledSwitchTooltip({ isVisible, isDefaultFixed })}>
                  <Switch
                    size='small'
                    checked={!!item.fixed}
                    onChange={checked => onFixedChange(item.key, checked)}
                    disabled={isDisabled}
                  />
                </Tooltip>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface DataIndexTooltipProps {
  dataIndex: ColumnSettingItem['dataIndex']
}

function DataIndexTooltip({ dataIndex }: DataIndexTooltipProps) {
  const { copied, copy } = useCopyToClipboard()
  const notAvailable = 'N/A'

  const getDataIndexDisplay = (dataIndex?: ColumnSettingItem['dataIndex']): string => {
    if (!dataIndex) return notAvailable
    if (Array.isArray(dataIndex)) return dataIndex.join('.')
    return dataIndex
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    const dataIndexValue = getDataIndexDisplay(dataIndex)
    if (dataIndexValue !== notAvailable) {
      copy(dataIndexValue)
    }
  }

  const stopPropagation = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => e.stopPropagation()

  const dataIndexValue = getDataIndexDisplay(dataIndex)

  if (dataIndexValue === notAvailable) {
    return <span onClick={stopPropagation}>N/A</span>
  }

  return (
    <Space>
      <span onClick={stopPropagation}>
        MongoDB field: <code>{dataIndexValue}</code>
      </span>
      <Button
        type='text'
        size='small'
        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
        onClick={handleCopy}
        style={{ color: copied ? '#52c41a' : 'inherit' }}
      />
    </Space>
  )
}

interface ModalFooterProps {
  onCancel: () => void
  onResetToDefault: () => void
  onApply: () => void
}

function ModalFooter({ onCancel, onResetToDefault, onApply }: ModalFooterProps) {
  return (
    <div className={styles.modalFooter}>
      <Button onClick={onResetToDefault} danger icon={<UndoOutlined />}>
        Reset to Default
      </Button>
      <Space>
        <Button onClick={onCancel} icon={<CloseOutlined />}>
          Cancel
        </Button>
        <Button type='primary' onClick={onApply} icon={<CheckOutlined />}>
          Apply
        </Button>
      </Space>
    </div>
  )
}
